import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ACTIONS } from 'src/constants/common';
import {
  type Actions,
  type State,
  storeReducer,
} from 'src/context/store/store-reducer';
import { useValuesToBoolean } from 'src/context/store/use-values-to-boolean';
import { useValuesToNumber } from 'src/context/store/use-values-to-number';
import { useWidgetData } from 'src/hooks/use-widget-data';
import { type CardTheme } from 'src/types/card-theme';
import { type AllowedCurrency, type CurrencyType } from 'src/types/currency';
import { type Experiment } from 'src/types/experiment';
import { type InfoAttrName } from 'src/types/info-attr-name';
import { Lang, type Locale } from 'src/types/lang-locale-type';
import { type ProductType } from 'src/types/product-type';
import { type StyleConfig } from 'src/types/style-config';
import { type ThemeType } from 'src/types/theme-type';
import { type WidgetOffers } from 'src/types/widget-offers';
import { checkCurrency, formatCurrency } from 'src/utils/check-currency';
import { checkInstallmentsCount } from 'src/utils/check-installments-count';
import { checkLang } from 'src/utils/check-lang';
import { checkLocale } from 'src/utils/check-locale';
import { checkPrice } from 'src/utils/check-price';
import { checkProductType } from 'src/utils/check-product-type';
import { checkTheme } from 'src/utils/check-theme';
import { findExperiment } from 'src/utils/find-experiment';

export interface MerchantProps<T extends ThemeType | CardTheme = ThemeType> {
  selector?: string;
  lang: Lang;
  locale: Locale;
  currency: CurrencyType;
  price: number;
  installmentsCount: number;
  publicKey?: string;
  merchantCode?: string;
  partnerCharges?: boolean;
  styleConfig?: Partial<StyleConfig>;
  /** Options for background color inheritting */
  shouldInheritBg?: boolean;
  isBig?: boolean;
  isDark?: boolean;
  productType?: ProductType;
  /**
   * @deprecated Has no effect on widget rendering. Accepted for backwards
   * compatibility with legacy merchant integrations. Do not use in new
   * integrations.
   */
  theme?: T;
  merchant?: string;
  minPrice?: number;
  minLimit?: number;
  titles?: string[];
  infoAttrName?: InfoAttrName;
}

export type LearnMoreType = 'ltf' | 'pix' | 'pif' | 'default';

export type StoreContextProps<T extends ThemeType | CardTheme = ThemeType> =
  MerchantProps<T> & {
    isRtl: boolean;
    formattedCurrency?: AllowedCurrency;
    priceRaw?: number;
    installmentsCountRaw?: number | string;
    isBold?: boolean;
    isMobileRounded?: boolean;
    isShortSplitIt?: boolean;
    experiments: Experiment[];
    entityId: string;
    offers: WidgetOffers;
    learnMoreVisibility: Record<LearnMoreType, boolean>;
    aid?: string;
    loading: boolean;
    infoAttrName?: InfoAttrName;
    dispatch: React.Dispatch<Actions>;
    getExperiment: typeof findExperiment;
  };

export const defaultValue = {} as StoreContextProps;

const StoreContext = React.createContext<StoreContextProps>(defaultValue);

StoreContext.displayName = 'StoreContext';

export const useAppContext = <T extends ThemeType | CardTheme>() =>
  React.useContext<StoreContextProps<T>>(
    StoreContext as any as React.Context<StoreContextProps<T>>
  );

export const INSTALLMENTS_COUNT_DEFAULT = 4;

type InitialStateProps = Omit<
  Partial<MerchantProps>,
  'merchant' | 'infoAttrName' | 'productType' | 'theme'
>;

const getInitialState = ({
  lang,
  locale,
  currency,
  price,
  installmentsCount,
  ...other
}: InitialStateProps): State => ({
  lang: checkLang(lang, {
    locale,
  }),
  locale: checkLocale(locale, { lang }),
  currency: checkCurrency(currency),
  formattedCurrency: formatCurrency(currency),
  price: checkPrice(price),
  priceRaw: price,
  installmentsCount: checkInstallmentsCount(INSTALLMENTS_COUNT_DEFAULT)(
    installmentsCount
  ),
  installmentsCountRaw: installmentsCount,
  isBig: false,
  isDark: false,
  isBold: false,
  isMobileRounded: false,
  isShortSplitIt: false,
  experiments: [],
  entityId: '',
  aid: '',
  offers: { installments: [] },
  learnMoreVisibility: {
    ltf: false,
    pix: false,
    pif: false,
    default: false,
  },
  loading: true,
  ...other,
});

export const AppProvider = <T extends ThemeType | CardTheme>({
  children,
  merchant = document.location.origin,
  infoAttrName,
  productType,
  theme,
  publicKey,
  merchantCode,
  styleConfig,
  selector,
  ...propsRaw
}: Partial<MerchantProps<T>> & {
  children: ReactNode;
}) => {
  const {
    shouldInheritBg = false,
    partnerCharges,
    ...props
  } = useValuesToBoolean(propsRaw);
  const { minPrice, minLimit, ...values } = useValuesToNumber(props);

  const [
    {
      lang,
      locale,
      currency,
      price,
      installmentsCount,
      installmentsCountRaw,
      formattedCurrency,
      priceRaw,
      isBig,
      isDark,
      titles,
      isBold,
      isMobileRounded,
      isShortSplitIt,
      experiments,
      entityId,
      aid,
      offers,
      learnMoreVisibility,
      loading,
    },
    dispatch,
  ] = useReducer(storeReducer, values, getInitialState);

  useEffect(() => {
    dispatch({
      type: ACTIONS.UPDATE_STORE,
      payload: {
        isDark: values.isDark,
        isBig: values.isBig,
        titles: values.titles,
        installmentsCount: values.installmentsCount,
        price: values.price,
        currency: values.currency,
        lang: values.lang,
        locale: values.locale,
      },
    });
  }, [
    dispatch,
    values.isDark,
    values.isBig,
    values.titles,
    values.installmentsCount,
    values.price,
    values.currency,
    values.lang,
    values.locale,
  ]);

  useWidgetData({
    publicKey,
    merchantCode,
    currency,
    amount: price,
    dispatch,
    loading,
  });

  const getExperiment = useCallback<StoreContextProps['getExperiment']>(
    (experimentName) => findExperiment(experimentName, experiments),
    [experiments]
  );

  const providerValue = useMemo(
    () => ({
      selector,
      isRtl: lang === Lang.ar,
      lang,
      locale,
      currency,
      price,
      productType: checkProductType(productType),
      theme: checkTheme(theme),
      installmentsCount,
      installmentsCountRaw,
      experiments,
      entityId,
      partnerCharges,
      publicKey,
      merchantCode,
      styleConfig,
      shouldInheritBg,
      isBig,
      isDark,
      merchant,
      formattedCurrency,
      priceRaw,
      dispatch,
      getExperiment,
      loading,
      offers,
      learnMoreVisibility,
      aid,
      minPrice: minPrice ?? minLimit,
      infoAttrName,
      titles,
      isBold,
      isMobileRounded,
      isShortSplitIt,
    }),
    [
      selector,
      currency,
      experiments,
      entityId,
      getExperiment,
      installmentsCount,
      installmentsCountRaw,
      lang,
      locale,
      merchantCode,
      partnerCharges,
      price,
      productType,
      publicKey,
      shouldInheritBg,
      styleConfig,
      theme,
      isBig,
      isDark,
      merchant,
      formattedCurrency,
      priceRaw,
      loading,
      offers,
      learnMoreVisibility,
      aid,
      minPrice,
      minLimit,
      infoAttrName,
      titles,
      isBold,
      isMobileRounded,
      isShortSplitIt,
    ]
  );

  return (
    <StoreContext.Provider value={providerValue}>
      {children}
    </StoreContext.Provider>
  );
};
